"use client";

import { useEffect, useState } from "react";
import { DepartmentStatusCode } from "../services/department-service";
import { ATTENDANCE_MODE, ATTENDANCE_MODE_OPTIONS } from "../../../constants/status";

export interface DepartmentFormValues {
  name: string;
  parent_department_id?: number;
  description: string;
  isActive: boolean;
  default_attendance_mode: ATTENDANCE_MODE;
}

interface DepartmentCreateModalProps {
  onClose: () => void;
  onSubmit: (v: DepartmentFormValues) => Promise<boolean>;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: DepartmentFormValues;
}

const DepartmentCreateModal: React.FC<DepartmentCreateModalProps> = ({
  onClose,
  onSubmit,
  isSubmitting,
  titleText = "Thêm mới bộ phận",
  submitText = "Thêm mới",
  initialValues,
}) => {
  const [form, setForm] = useState<DepartmentFormValues>(
    initialValues ?? {
      name: "",
      parent_department_id: 0,
      description: "",
      isActive: true,
      default_attendance_mode: ATTENDANCE_MODE.SHIFT,
    }
  );

  const handleChange = (field: keyof DepartmentFormValues, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const ok = await onSubmit(form);
    if (ok) onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <h2 className="mb-4 text-lg font-semibold text-gray-800">{titleText}</h2>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tên bộ phận
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Mô tả
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Chế độ chấm công mặc định
            </label>
            {/*
              Áp dụng cho mọi NV thuộc bộ phận này, trừ khi bị ghi đè riêng ở hồ sơ nhân viên
              (xem select "Chế độ chấm công riêng" trong form nhân viên).
            */}
            <select
              value={form.default_attendance_mode}
              onChange={(e) => handleChange("default_attendance_mode", Number(e.target.value) as ATTENDANCE_MODE)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:ring-green-500"
            >
              {ATTENDANCE_MODE_OPTIONS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>
          {/*
            "Bộ phận tài xế" không còn là checkbox ở đây — trước kia giới hạn CHỈ 1 bộ phận được đánh
            dấu. Nay "là tài xế" là quyền DRIVER, gán được cho NHIỀU bộ phận qua màn "Phân quyền theo
            bộ phận" sau khi tạo xong (không giới hạn số bộ phận).
          */}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Hủy
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleSubmit}
            className="rounded-md bg-green-500 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Đang xử lý..." : submitText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentCreateModal;
